import { ref, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useFiltersStore, useWorkspaceStore } from "@/store";
import { getRouter } from "@/router";
import { logError } from "@/loggers";
import { FEATURE_NAMES } from "@/loggers/types";

/** Query params that describe the table state rather than a filter, so a saved workspace must not carry them. */
export const NON_FILTER_QUERY_KEYS = ["tSearch", "page", "limit"];

interface UseSaveWorkspaceOptions {
  /** Route query keys left out of the saved `query-params` entries. */
  excludeQueryKeys?: string[];
  /** Extra workspace data entries (e.g. user columns) appended to the payload. */
  extraData?: () => unknown[];
}

/**
 * Builds and persists a workspace from the applied filters and route query.
 * Shared by the Shipments "Save as workspace" modal and the Orders "Save filters" action.
 */
export const useSaveWorkspace = (
  workspaceType: string,
  activeAppliedFilter: Ref<Record<string, unknown> | undefined>,
  options: UseSaveWorkspaceOptions = {}
) => {
  const { t } = useI18n();
  const workspaceStore = useWorkspaceStore();
  const filtersStore = useFiltersStore();
  const { appliedFilters } = storeToRefs(filtersStore);
  const { workspaceTable } = storeToRefs(workspaceStore);
  const error = ref("");

  const isDuplicate = (name: string): boolean =>
    Boolean(workspaceTable.value?.userFilters?.some((workspace) => workspace.name === name));

  const buildPayload = (name: string) => {
    const router = getRouter();
    const query = router?.currentRoute?.value?.query ?? {};
    const excluded = options.excludeQueryKeys ?? [];

    const queryParamsData = Object.entries(query)
      .filter(([key]) => !appliedFilters.value.some((filter) => filter?.id === key) && !excluded.includes(key))
      .map(([key, value]) => ({ type: "query-params", id: key, value }));

    const filtersWorkspacePayload = Object.entries(activeAppliedFilter.value || {}).map(([key, value]) => ({
      id: key,
      value,
      type: "all-filters"
    }));

    return {
      name,
      type: workspaceType,
      data: [...appliedFilters.value, ...filtersWorkspacePayload, ...queryParamsData, ...(options.extraData?.() ?? [])]
    };
  };

  /** Persists the workspace and navigates to it. Resolves true on success. */
  const save = async (name: string): Promise<boolean> => {
    if (!name) return false;
    error.value = "";
    const router = getRouter();
    try {
      await workspaceStore.addWorkspace(buildPayload(name));
      const added = workspaceStore.workspaceTable?.userFilters?.find((workspace) => workspace.name === name);
      router.push({ path: added.routePath, query: router.currentRoute.value.query });
    } catch (e) {
      error.value = t("ERROR_SAVING_WORKSPACE");
      logError(FEATURE_NAMES.MVS_WORKSPACES, `Error saving workspace [${workspaceType}]: ${e}`);
      return false;
    }
    return true;
  };

  return { save, buildPayload, isDuplicate, error };
};
